import request from '@/utils/request'

//#region 运维诊断日报
// 接口文档：docs/alarm-daily-report-frontend-api.html
// 基础路径：/device/v3/station/alarm/daily-reports
// 响应约定：业务层拿到的是 res.data —— R<T> 的 data 或 TableDataInfo 整体

/**
 * 查询指定日报（也用于生成后状态轮询）
 * @param {number|string} reportId - 日报ID（取待办消息的 messageId）
 * @returns {Promise<Object>} 日报对象；非 READY 状态只保证返回 reportId/reportDate/status/errorMessage
 */
export function getDailyReport(reportId) {
  return request({
    url: `/device/v3/station/alarm/daily-reports/${reportId}`,
    method: 'get'
  })
}

/**
 * 电站聚合分页
 * @param {Object} params
 * @param {number} params.reportId - 日报ID（必填）
 * @param {string} [params.level] - 电站等级筛选：ALL/P0/P1/P2，空值等价 ALL
 * @param {string} [params.keyword] - 按电站名称模糊搜索
 * @param {Array<number>|string} [params.stationIds] - 指定电站ID，多个ID以逗号分隔
 * @param {number} [params.pageNum=1] - 页码，最小1
 * @param {number} [params.pageSize=20] - 每页条数，范围1-100
 * @returns {Promise<{total:number, rows:Array}>} TableDataInfo
 */
export function getDailyReportStations(params) {
  return request({
    url: `/device/v3/station/alarm/daily-reports/${params.reportId}/stations`,
    method: 'get',
    params: {
      level: params.level,
      keyword: params.keyword,
      stationIds: Array.isArray(params.stationIds) ? params.stationIds.join(',') : params.stationIds,
      pageNum: params.pageNum,
      pageSize: params.pageSize
    }
  })
}

/**
 * 站内告警分页（电站+设备+告警编码已聚合，返回 triggerCount）
 * @param {Object} params
 * @param {number} params.reportId - 日报ID（必填）
 * @param {number} params.stationId - 电站ID（必填，且必须属于该日报）
 * @param {string} [params.category] - 告警分类筛选：ALL/SAFETY/INCOME/NORMAL
 * @param {string} [params.keyword] - 匹配设备名/设备UUID/告警编码/告警描述
 * @param {number} [params.pageNum=1] - 页码
 * @param {number} [params.pageSize=20] - 每页条数，最大100
 * @returns {Promise<{total:number, rows:Array}>} TableDataInfo
 */
export function getStationAlarms(params) {
  return request({
    url: `/device/v3/station/alarm/daily-reports/${params.reportId}/stations/${params.stationId}/alarms`,
    method: 'get',
    params: {
      category: params.category,
      keyword: params.keyword,
      pageNum: params.pageNum,
      pageSize: params.pageSize
    }
  })
}

/**
 * 获取电站告警智能分析上下文
 * @param {Object} params
 * @param {number} params.reportId - 日报ID（必填）
 * @param {number} params.stationId - 电站ID（必填）
 * @returns {Promise<Object>} R<AlarmAnalysisContext>
 */
export function getStationAnalysisContext(params) {
  return request({
    url: `/device/v3/station/alarm/daily-reports/${params.reportId}/stations/${params.stationId}/analysis-context`,
    method: 'get'
  })
}

/**
 * 高频告警聚类分页
 * @param {Object} params
 * @param {number} params.reportId - 日报ID（必填，日报必须为 READY）
 * @param {number} [params.pageNum=1] - 页码
 * @param {number} [params.pageSize=20] - 每页条数，最大100
 * @returns {Promise<Object>} { summary, items, total, pageNum, pageSize, hasMore }
 */
export function getDailyReportClusters(params) {
  return request({
    url: `/device/v3/station/alarm/daily-reports/${params.reportId}/clusters`,
    method: 'get',
    params: {
      pageNum: params.pageNum,
      pageSize: params.pageSize
    }
  })
}

/**
 * 按告警编码下钻分页
 * @param {Object} params
 * @param {number} params.reportId - 日报ID（必填，日报必须为 READY）
 * @param {string} params.alarmCode - 告警编码（必填，不允许空白）
 * @param {string} [params.state] - 告警状态筛选：ALL/UNRECOVERED/RECOVERED/AUTO_RECOVERED
 * @param {number} [params.pageNum=1] - 页码
 * @param {number} [params.pageSize=20] - 每页条数，最大100
 * @returns {Promise<Object>} { header, items, pageNum, pageSize, hasMore }
 */
export function getDailyReportAlarms(params) {
  return request({
    url: `/device/v3/station/alarm/daily-reports/${params.reportId}/alarms`,
    method: 'get',
    params: {
      alarmCode: params.alarmCode,
      state: params.state,
      pageNum: params.pageNum,
      pageSize: params.pageSize
    }
  })
}

// 备注：POST /device/v3/station/alarm/daily-reports/generate（手动生成/强制重算）
// 本期不对接——设计方案 2.1 明确不提供手动刷新入口。接口就绪后在此追加：
// export function generateDailyReport(data) {
//   return request({ url: '/device/v3/station/alarm/daily-reports/generate', method: 'post', data })
// }

//#endregion
